<template>
    <div class="space-y-6">
        <!-- Page Header -->
        <div class="text-center mb-8">
            <!-- Icon -->
            <div class="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
            </div>

            <h1 class="text-3xl font-bold text-secondary-900 dark:text-white mb-2">
                Forgot Password?
            </h1>
            <p class="text-secondary-600 dark:text-secondary-400">
                No worries, we'll send you reset instructions
            </p>
        </div>

        <!-- Success Message -->
        <div
            v-if="status"
            class="mb-6 p-4 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg"
        >
            <div class="flex items-start space-x-3">
                <svg class="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                <div>
                    <p class="text-sm text-success-800 dark:text-success-200 font-medium">
                        Password reset link sent!
                    </p>
                    <p class="text-sm text-success-700 dark:text-success-300 mt-1">
                        {{ status }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Form -->
        <form @submit.prevent="submit" class="space-y-6">
            <!-- Email Address -->
            <div>
                <label for="email" class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Email Address
                </label>
                <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg class="h-5 w-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                    </div>
                    <input
                        id="email"
                        ref="emailInput"
                        v-model="form.email"
                        type="email"
                        autocomplete="email"
                        required
                        :class="[
                            'input pl-10',
                            form.errors.email ? 'border-danger-300 dark:border-danger-600 focus:border-danger-500 focus:ring-danger-500' : ''
                        ]"
                        placeholder="john@example.com"
                        @blur="validateEmail"
                    />
                </div>
                <p v-if="form.errors.email" class="mt-2 text-sm text-danger-600 dark:text-danger-400">
                    {{ form.errors.email }}
                </p>
                <p v-else class="mt-2 text-sm text-secondary-500 dark:text-secondary-500">
                    Enter the email address associated with your account
                </p>
            </div>

            <!-- Submit Button -->
            <button
                type="submit"
                :disabled="form.processing || !isFormValid"
                class="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <svg
                    v-if="form.processing"
                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-if="form.processing">Sending Reset Link...</span>
                <span v-else>Send Reset Link</span>
            </button>
        </form>

        <!-- Divider -->
        <div class="relative my-6">
            <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-secondary-200 dark:border-secondary-700"></div>
            </div>
            <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white dark:bg-secondary-800 text-secondary-500 dark:text-secondary-400">
                    or
                </span>
            </div>
        </div>

        <!-- Back to Login -->
        <Link
            href="/login"
            class="block w-full text-center px-4 py-2.5 border border-secondary-300 dark:border-secondary-600 rounded-lg text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-800 transition-colors font-medium"
        >
            <svg class="inline-block w-4 h-4 mr-2 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Login
        </Link>

        <!-- Help Section -->
        <div class="mt-8 p-4 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg">
            <div class="flex items-start space-x-3">
                <svg class="w-5 h-5 text-info-600 dark:text-info-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
                <div class="text-sm">
                    <p class="text-info-800 dark:text-info-200 font-medium mb-1">
                        Can't access your email?
                    </p>
                    <p class="text-info-700 dark:text-info-300">
                        Contact support at
                        <a href="mailto:support@marginresolve.com" class="underline hover:text-info-900 dark:hover:text-info-100">
                            support@marginresolve.com
                        </a>
                        for assistance.
                    </p>
                </div>
            </div>
        </div>

        <!-- Security Note -->
        <div class="mt-4 p-3 bg-secondary-50 dark:bg-secondary-900/50 rounded-lg">
            <p class="text-xs text-secondary-600 dark:text-secondary-400 text-center">
                <svg class="inline-block w-4 h-4 mr-1 -mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
                </svg>
                For your security, password reset links expire after 60 minutes
            </p>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Link, useForm } from '@inertiajs/vue3';

// Props
const props = defineProps({
    status: {
        type: String,
        default: null,
    },
});

// Form
const form = useForm({
    email: '',
});

// Refs
const emailInput = ref(null);

// Email validation
const emailError = ref('');
const validateEmail = () => {
    emailError.value = '';

    if (!form.email) {
        emailError.value = 'Email address is required';
        return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
        emailError.value = 'Please enter a valid email address';
        return false;
    }

    return true;
};

// Form validation
const isFormValid = computed(() => {
    return form.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
});

// Submit form
const submit = () => {
    if (!validateEmail()) {
        return;
    }

    form.post('/forgot-password', {
        preserveScroll: true,
        onSuccess: () => {
            // Success message is shown via status prop
        },
        onError: (errors) => {
            // Errors are automatically set on form.errors
            if (emailInput.value) {
                emailInput.value.focus();
            }
        },
    });
};

// Auto-focus on mount
onMounted(() => {
    if (emailInput.value) {
        emailInput.value.focus();
    }
});
</script>

<style scoped>
/* Smooth transitions */
* {
    transition-property: color, background-color, border-color, opacity;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
}
</style>
